import React, { Dispatch, ReactNode, SetStateAction } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { t, Trans } from "@lingui/macro";
import { Row, Space, Tooltip, Typography } from "antd";
import { isEmpty, keyBy, startCase } from "lodash";
import get from "lodash/get";
import styled from "styled-components";

import EditPermissionLink, { PermissionDetails } from "components/permissions/edit-link";
import TableCheckbox from "components/table/checkbox";

const NameContainer = styled.div<{ width: string }>`
  max-width: ${(props) => props.width};
  :first-letter {
    text-transform: uppercase;
  }

  .ant-typography {
    padding-bottom: 3px;
    :first-letter {
      text-transform: uppercase;
    }
  }
`;

const CapitalizeFirstLetter = styled.div`
  :first-letter {
    text-transform: uppercase;
  }
`;

const CheckboxWithLabel = styled(TableCheckbox)`
  max-width: 100%;

  .ant-typography-ellipsis {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export interface Column {
  accessor: string;
  sticky?: "left" | "right";
  Header: ReactNode;
  dataIndex?: string;
  render?: (data: any) => ReactNode;
}

interface Permission {
  permissions: { [key: string]: { has_permission: boolean; read_only: boolean } };
}

const generateColumns = (
  permissionDetails: PermissionDetails,
  nameIndex: string,
  options: Column[],
  permissions: Permission[],
  recordsLength: number,
  onColumnCheck: (key: string, value: boolean, permissions: Permission[]) => void,
  onCellCheck: (owner: string, key: string, value: boolean) => void,
  onRowChecked: (owner: string, value: boolean, permissions: Permission[]) => void,
  onSelectAll: (value: boolean) => void
) => {
  const columns: Column[] = options
    ? Object.keys(options).map((key: any) => {
        const checked =
          recordsLength === permissions.length &&
          permissions.every((permission: Permission) => {
            return permission?.permissions[key]?.has_permission === true;
          });

        const disabled =
          recordsLength === permissions.length &&
          permissions.every((permission: Permission) => {
            return permission?.permissions[key]?.read_only === true;
          });

        // @ts-ignore
        const text = options[key]?.label || startCase(key);
        /*@ts-ignore*/
        const helpText = get(options[key], "help_text");

        return {
          accessor: key,
          sticky: "left",
          resizable: false,
          tooltip: <CapitalizeFirstLetter>{text}</CapitalizeFirstLetter>,
          Header: (
            <Row align={"middle"} justify={"space-between"}>
              <Space>
                <TableCheckbox
                  checked={checked}
                  disabled={disabled}
                  onChange={(value: { target: { checked: boolean } }) =>
                    onColumnCheck(key, value.target.checked, permissions)
                  }
                />
                <Typography.Text ellipsis={true} style={{ width: "100%" }}>
                  {text}
                </Typography.Text>
                {helpText && (
                  <Tooltip title={helpText} trigger={["click"]}>
                    <InfoCircleOutlined />
                  </Tooltip>
                )}
              </Space>
            </Row>
          ),
          render: (data: {
            id: string;
            url: string;
            name_display: string;
            name: string;
            permissions: { [key: string]: { has_permission: boolean; read_only: boolean } };
          }) => {
            const checked = data?.permissions?.[key]?.has_permission;
            const readOnly = data?.permissions?.[key]?.read_only;

            return (
              <Space>
                <TableCheckbox
                  checked={checked}
                  disabled={readOnly}
                  onChange={(value: { target: { checked: boolean } }) =>
                    onCellCheck(data.url, key, value.target.checked)
                  }
                />
                {readOnly && key && (
                  <EditPermissionLink
                    permission={key}
                    permissionValue={data.id}
                    permissionDetails={permissionDetails}
                  />
                )}
              </Space>
            );
          },
        };
      })
    : [];

  const checked = permissions.every((row: any) => {
    const keys = Object.keys(row.permissions);
    const permissionsLength = isEmpty(options) ? 0 : Object.keys(options)?.length;

    return keys?.length === permissionsLength && keys.every((key) => row.permissions[key]?.has_permission === true);
  });

  const disabled = permissions.every((row: any) => {
    const keys = Object.keys(row.permissions);
    const permissionsLength = isEmpty(options) ? 0 : Object.keys(options)?.length;

    return keys?.length === permissionsLength && keys.every((key) => row.permissions[key]?.read_only === true);
  });

  columns.unshift({
    accessor: "name",
    sticky: "left",
    Header: (
      <Space>
        <TableCheckbox
          checked={checked}
          disabled={disabled}
          onChange={(value: { target: { checked: boolean } }) => onSelectAll(value.target.checked)}
        />
        <Typography.Text>
          <Trans>{t`Name`}</Trans>
        </Typography.Text>
      </Space>
    ),
    render: (data: any) => {
      const keys = Object.keys(data.permissions);
      const permissionsLength = isEmpty(options) ? 0 : Object.keys(options)?.length;
      const checked = options && Object.keys(options).every((key) => data.permissions?.[key]?.has_permission === true);
      const disabled =
        keys?.length === permissionsLength &&
        Object.values(data.permissions).every((permission: any) => permission.read_only === true);

      return (
        <Space>
          <CheckboxWithLabel
            checked={checked}
            disabled={disabled}
            onChange={(value: { target: { checked: boolean } }) =>
              onRowChecked(data?.url, value.target.checked, data.permissions)
            }
          />
          <NameContainer width={"205px"}>
            <Typography.Text ellipsis={{ tooltip: data?.[nameIndex] }}>{data?.[nameIndex]}</Typography.Text>
          </NameContainer>
        </Space>
      );
    },
  });

  return columns;
};

const generateRecords = (permissions: unknown[], owners: object[], iteratee: string) => {
  const keyedPermissions = keyBy(permissions, iteratee);

  return !isEmpty(owners)
    ? owners?.map((owner) => {
        return {
          ...owner,
          // @ts-ignore
          permissions: keyedPermissions[owner.url]?.permissions,
        };
      })
    : [];
};

const transformPermissionsToList = (obj: { [key: string]: any }, index: string) => {
  return Object.keys(obj).map((key) => {
    return {
      [index]: key,
      permissions: obj[key],
    };
  });
};

const onSelectRow = (
  owner: string,
  value: boolean,
  permissions: { [key: string]: any }[],
  availablePermission: { [key: string]: any },
  updated: { [key: string]: any },
  updatePermission: Dispatch<SetStateAction<any>>
) => {
  const fields = Object.keys(permissions);
  const res: { [key: string]: { has_permission: boolean } } = {};

  fields.map((field: string) => {
    if (!availablePermission[field]?.read_only) {
      res[field] = {
        has_permission: value,
      };
    }
  });

  updated[owner] = res;
  updatePermission({ ...updated });
};

const onSelectColumn = (
  key: string,
  value: boolean,
  permissions: { [key: string]: any }[],
  updated: { [key: string]: any },
  updatePermission: Dispatch<SetStateAction<any>>,
  dataIndex: string
) => {
  permissions.forEach((permission) => {
    if (!permission.permissions[key]?.read_only) {
      updated[permission[dataIndex]] = {
        ...updated[permission[dataIndex] as string],
        [key]: { has_permission: value },
      };
    }
  });

  updatePermission({ ...updated });
};

const onSelectCell = (
  owner: string,
  key: string,
  value: boolean,
  updated: { [key: string]: any },
  updatePermission: Dispatch<SetStateAction<any>>
) => {
  updated[owner] = {
    ...updated[owner],
    [key]: { has_permission: value },
  };
  updatePermission({ ...updated });
};

const onSelectTable = (
  value: boolean,
  columns: Column[],
  permissions: { [key: string]: any }[],
  updated: { [key: string]: any },
  updatePermission: Dispatch<SetStateAction<any>>,
  iteratee: string
) => {
  permissions.map((permission: { [key: string]: any }) => {
    const newPermission: { [key: string]: any } = {};
    Object.keys(columns).map((key) => {
      if (!permission.permissions[key]?.read_only) {
        newPermission[key] = { has_permission: value };
      }
    });
    updated[permission[iteratee]] = newPermission;
  });

  updatePermission({ ...updated });
};

const mergePermissions = (
  initial: { [key: string]: any }[],
  records: { [key: string]: any }[],
  updates: { [key: string]: any },
  dataIndex: string
) => {
  const keyedPermissions = keyBy(initial, dataIndex);
  return records.map((row: any) => {
    const res = {
      [dataIndex]: row.url,
      permissions: {},
    };
    const permission = updates[row.url];
    const existing = keyedPermissions[row.url]?.permissions;

    if (existing) {
      res["permissions"] = {
        ...existing,
      };
    }

    if (permission) {
      res["permissions"] = {
        ...res["permissions"],
        ...permission,
      };
    }

    return res;
  });
};

export {
  mergePermissions,
  generateColumns,
  generateRecords,
  transformPermissionsToList,
  onSelectTable,
  onSelectCell,
  onSelectColumn,
  onSelectRow,
};
